import { useState } from "react";
import PropTypes from "prop-types";
import { DateTimePicker } from "@mui/x-date-pickers";
import { Grid } from "@mui/material";
import dayjs from "dayjs";
import useForm from "../common/AsyncFormBox/useForm";
import { getInitialDateTimeValue } from "./internal/dateInputUtils";

DateTimeRangeInput.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  labelFrom: PropTypes.node,
  labelTo: PropTypes.node,
  defaultValue: PropTypes.array,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  maxDate: PropTypes.instanceOf(dayjs),
  size: PropTypes.oneOf(["small", "medium"]),
};

/**
 * DateTimeRangeInput component for selecting a date/time range
 * @param {Object} props
 * @param {string} props.name - Name for the form field
 * @param {string} [props.label] - Shared label for both fields
 * @param {string} [props.labelFrom] - Label for the "from" field
 * @param {string} [props.labelTo] - Label for the "to" field
 * @param {Array} [props.defaultValue] - Default value as [from, to] dayjs objects or ISO strings
 * @param {Function} [props.onChange] - Callback when value changes, receives [from, to] array
 * @param {boolean} [props.required] - Whether the fields are required
 * @param {dayjs} [props.maxDate] - Maximum date
 * @param {boolean} [props.readOnly] - Whether fields are read-only
 * @param {string} [props.size] - Size of the input fields ("small" or "medium")
 */
function DateTimeRangeInput({
  name,
  label,
  labelFrom,
  labelTo,
  defaultValue,
  onChange,
  required,
  maxDate,
  readOnly,
  size,
}) {
  const form = useForm();
  const [valueFrom, setValueFrom] = useState(getInitialDateTimeValue(defaultValue?.[0], name + "_from", form));
  const [valueTo, setValueTo] = useState(getInitialDateTimeValue(defaultValue?.[1], name + "_to", form));

  function handleSetValueFrom(newValue) {
    if (!newValue || !newValue.isValid()) return;

    const onChangeValue = [newValue, valueTo];
    setValueFrom(newValue);
    if (valueTo && newValue > valueTo) {
      setValueTo(null);
      onChangeValue[1] = null;
    }

    form?.setValue(name, onChangeValue);
    onChange && onChange(onChangeValue);
  }

  function handleSetValueTo(newValue) {
    if (!newValue || !newValue.isValid()) return;

    setValueTo(newValue);
    const onChangeValue = [valueFrom, newValue];
    form?.setValue(name, onChangeValue);
    onChange && onChange(onChangeValue);
  }

  return (
    <Grid container spacing={1}>
      <Grid size={{ xs: 6 }}>
        <DateTimePicker
          ampm={false}
          format={"DD.MM.YYYY HH:mm"}
          slotProps={{
            textField: { fullWidth: true, required: required, name: name + "_from", size, InputProps: { readOnly } },
          }}
          label={labelFrom || label}
          value={valueFrom}
          onChange={handleSetValueFrom}
          maxDate={maxDate}
        />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <DateTimePicker
          ampm={false}
          format={"DD.MM.YYYY HH:mm"}
          slotProps={{
            textField: {
              fullWidth: true,
              required: required || !!valueFrom,
              name: name + "_to",
              size,
              InputProps: { readOnly },
            },
          }}
          label={labelTo || label}
          value={valueTo}
          onChange={handleSetValueTo}
          minDate={valueFrom}
          maxDate={maxDate}
        />
      </Grid>
    </Grid>
  );
}

export default DateTimeRangeInput;
